'use client'

import {
  add,
  eachMonthOfInterval,
  endOfYear,
  format,
  isBefore,
  isEqual,
  isFuture,
  parse,
  startOfMonth,
  startOfToday,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarDaysIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { cn } from '@/lib/cn'

import { Button, buttonVariants } from '../button'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'

function getStartOfCurrentMonth() {
  return startOfMonth(startOfToday())
}

type Props = {
  currentMonth?: Date
  onMonthChange?: (newMonth: Date) => void

  disabled?: boolean
  minDate?: Date
  className?: string
  isError?: boolean

  isRange?: boolean
  currentMonthRange?: [Date | null, Date | null]
  onRangeChange?: (range: [Date | null, Date | null]) => void
  popoverSide?: 'top' | 'right' | 'bottom' | 'left'
}

const MonthPicker = ({
  currentMonth,
  onMonthChange,
  disabled = false,
  minDate,
  className,
  isError = false,
  isRange = false,
  currentMonthRange = [new Date(), new Date()],
  onRangeChange,
  popoverSide,
}: Props) => {
  const [open, setOpen] = useState(false)

  const displayDate = currentMonth ?? getStartOfCurrentMonth()
  const [currentYear, setCurrentYear] = useState(format(displayDate, 'yyyy'))
  const firstDayCurrentYear = parse(currentYear, 'yyyy', new Date())

  const months = eachMonthOfInterval({
    start: firstDayCurrentYear,
    end: endOfYear(firstDayCurrentYear),
  })

  const [hoveredMonth, setHoveredMonth] = useState<Date | null>(null)

  const monthText = useMemo(() => {
    if (!currentMonth) return 'Selecione o mês'
    const formattedMonth = format(currentMonth, 'MMM/yyyy', { locale: ptBR })
    return formattedMonth.charAt(0).toUpperCase() + formattedMonth.slice(1)
  }, [currentMonth])

  function previousYear() {
    const firstDayNextYear = add(firstDayCurrentYear, { years: -1 })
    setCurrentYear(format(firstDayNextYear, 'yyyy'))
  }

  function nextYear() {
    const firstDayNextYear = add(firstDayCurrentYear, { years: 1 })
    setCurrentYear(format(firstDayNextYear, 'yyyy'))
  }

  const handleMonthClick = (selectedMonth: Date) => {
    if (!isRange) {
      onMonthChange?.(selectedMonth)
      setOpen(false)
      return
    }

    const [start, end] = currentMonthRange

    if (!start || (start && end)) {
      onRangeChange?.([selectedMonth, null])
      return
    }

    if (selectedMonth.getTime() < start.getTime()) {
      onRangeChange?.([selectedMonth, start])
      setOpen(false)
      return
    }

    onRangeChange?.([start, selectedMonth])
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label="calendar"
          variant="secondary"
          disabled={disabled}
          className={cn(
            'justify-start text-left gap-2 border-system-border-primary font-normal text-system-text-primary',
            !currentMonth && 'text-system-text-secondary',
            isError && 'border-system-state-error',
            className,
          )}
        >
          <CalendarDaysIcon className="size-4" />
          {isRange && currentMonthRange?.[0] && currentMonthRange?.[1]
            ? `${format(currentMonthRange[0], 'MMM/yyyy', {
                locale: ptBR,
              })} - ${format(currentMonthRange[1], 'MMM/yyyy', {
                locale: ptBR,
              })}`
            : monthText}
        </Button>
      </PopoverTrigger>
      <PopoverContent side={popoverSide}>
        <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
          <div className="space-y-4">
            <div className="relative flex items-center justify-center">
              <div
                className="text-sm font-medium text-system-text-primary"
                aria-live="polite"
                role="presentation"
                id="month-picker"
              >
                {format(firstDayCurrentYear, 'yyyy')}
              </div>
              <div className="flex items-center space-x-1">
                <button
                  name="previous-year"
                  aria-label="Go to previous year"
                  className={cn(
                    buttonVariants({ variant: 'secondary' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                    'absolute left-1',
                  )}
                  type="button"
                  onClick={previousYear}
                >
                  <ChevronLeft className="size-4" />
                </button>
                <button
                  name="next-year"
                  aria-label="Go to next year"
                  className={cn(
                    buttonVariants({ variant: 'secondary' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                    'absolute right-1 disabled:bg-slate-100',
                  )}
                  type="button"
                  disabled={isFuture(add(firstDayCurrentYear, { years: 1 }))}
                  onClick={nextYear}
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>

            <div
              className="grid w-full grid-cols-3 gap-2"
              role="grid"
              aria-labelledby="month-picker"
            >
              {months.map((month) => {
                const isSelectedSingle =
                  !isRange && !!currentMonth && isEqual(month, currentMonth)

                const [startRange, endRange] = currentMonthRange ?? [null, null]

                const isStart =
                  isRange && startRange && isEqual(month, startRange)
                const isEnd = isRange && endRange && isEqual(month, endRange)
                const isInRange =
                  isRange &&
                  startRange &&
                  endRange &&
                  month.getTime() > startRange.getTime() &&
                  month.getTime() < endRange.getTime()

                const isHoverInRange =
                  isRange &&
                  startRange &&
                  !endRange &&
                  hoveredMonth &&
                  (() => {
                    let lower = startRange.getTime()
                    let upper = hoveredMonth.getTime()
                    if (lower > upper) [lower, upper] = [upper, lower]
                    return month.getTime() > lower && month.getTime() < upper
                  })()

                const isBeforeMinDate =
                  !!minDate &&
                  isBefore(startOfMonth(month), startOfMonth(minDate))

                return (
                  <div
                    key={month.toString()}
                    className="relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                    role="presentation"
                  >
                    <button
                      name="day"
                      onMouseEnter={() => {
                        if (isRange && startRange && !endRange) {
                          setHoveredMonth(month)
                        }
                      }}
                      onMouseLeave={() => {
                        if (isRange && startRange && !endRange) {
                          setHoveredMonth(null)
                        }
                      }}
                      className={cn(
                        'inline-flex h-9 w-16 items-center justify-center rounded-md p-0 text-sm font-normal ring-offset-white transition-colors hover:bg-slate-100 hover:text-system-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-system-text-primary',
                        !isRange &&
                          isSelectedSingle &&
                          'bg-system-primary-purple text-white hover:bg-system-primary-purple hover:text-white focus:bg-system-primary-purple focus:text-white',
                        isRange &&
                          (isStart || isEnd) &&
                          'bg-system-primary-purple text-white',
                        isRange &&
                          isInRange &&
                          'bg-slate-100 text-system-text-primary',
                        isRange &&
                          isHoverInRange &&
                          'bg-slate-100 text-system-text-primary',
                        !isRange &&
                          !isSelectedSingle &&
                          isEqual(month, getStartOfCurrentMonth()) &&
                          'bg-slate-100 text-system-text-primary',
                      )}
                      disabled={isFuture(month) || isBeforeMinDate}
                      role="gridcell"
                      aria-label={format(month, 'MMM')}
                      tabIndex={-1}
                      type="button"
                      onClick={() => handleMonthClick(month)}
                    >
                      <time dateTime={format(month, 'yyyy-MM-dd')}>
                        {format(month, 'MMM', { locale: ptBR })}
                      </time>
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

MonthPicker.displayName = 'MonthPicker'

export { MonthPicker }
